import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Plus, Minus } from "lucide-react";

const KEY = "sr_audio";
const load = () => { try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch { return {}; } };

export default function AmbientAudio() {
  const ref = useRef(null);
  const saved = load();
  const [volume, setVolume] = useState(typeof saved.volume === "number" ? saved.volume : 0.3);
  const [muted, setMuted] = useState(!!saved.muted);
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    a.volume = volume;
    a.muted = muted;
    localStorage.setItem(KEY, JSON.stringify({ volume, muted }));
  }, [volume, muted]);

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    let timer;
    const tryPlay = () => a.play().then(() => { setPlaying(true); setBlocked(false); }).catch(() => setBlocked(true));
    tryPlay();
    const events = ["pointerdown", "pointerup", "touchend", "keydown", "click", "wheel", "scroll"];
    const onGesture = () => { if (a.paused) tryPlay(); };
    events.forEach((e) => window.addEventListener(e, onGesture, { passive: true, capture: true }));
    const onVisible = () => { if (document.visibilityState === "visible" && a.paused) tryPlay(); };
    document.addEventListener("visibilitychange", onVisible);
    timer = setInterval(() => { if (a.paused) tryPlay(); else clearInterval(timer); }, 2500);
    return () => {
      events.forEach((e) => window.removeEventListener(e, onGesture, { capture: true }));
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- autoplay bootstrap runs once on mount
  }, []);

  const step = (d) => {
    setVolume((v) => Math.min(1, Math.max(0, Math.round((v + d) * 10) / 10)));
    setMuted(false);
  };
  const toggleMute = () => {
    const a = ref.current;
    setMuted((m) => !m);
    if (a?.paused) a.play().then(() => setPlaying(true)).catch(() => {});
  };

  return (
    <>
      <audio ref={ref} src="/audio/ambient.mp3" loop preload="auto" autoPlay data-testid="ambient-audio" />
      {blocked && !playing && !muted && (
        <button
          onClick={() => ref.current?.play().then(() => { setPlaying(true); setBlocked(false); }).catch(() => {})}
          className="fixed bottom-[8.25rem] md:bottom-[4.5rem] right-4 z-[60] flex items-center gap-2 rounded-full border border-gold-active bg-[#080706]/90 backdrop-blur-xl pl-3 pr-4 py-2 text-[11px] uppercase tracking-[0.18em] text-gold-hi shadow-[0_8px_30px_rgba(0,0,0,0.6)] animate-fade-up"
          data-testid="audio-tap-hint"
        >
          <span className="relative flex w-2.5 h-2.5"><span className="absolute inset-0 rounded-full bg-gold-hi animate-ping opacity-60" /><span className="relative rounded-full w-2.5 h-2.5 bg-gold" /></span>
          Tap for divine sound
        </button>
      )}
      <div
        className="fixed bottom-20 md:bottom-5 right-4 z-[60] flex items-center gap-1 rounded-full border border-gold bg-[#080706]/85 backdrop-blur-xl px-2 py-1.5 shadow-[0_8px_30px_rgba(0,0,0,0.6)]"
        data-testid="audio-controls"
      >
        <button onClick={() => step(-0.1)} aria-label="Volume down" className="w-8 h-8 rounded-full flex items-center justify-center text-gold hover:text-gold-hi hover:bg-gold/10 transition" data-testid="audio-volume-down">
          <Minus size={14} />
        </button>
        <button onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"} className={`relative w-9 h-9 rounded-full flex items-center justify-center border transition ${muted ? "border-gold/40 text-muted-warm" : "border-gold-active text-gold-hi"}`} data-testid="audio-mute-toggle">
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          {playing && !muted && <span className="absolute inset-0 rounded-full border border-gold-active animate-ping opacity-30" />}
        </button>
        <button onClick={() => step(0.1)} aria-label="Volume up" className="w-8 h-8 rounded-full flex items-center justify-center text-gold hover:text-gold-hi hover:bg-gold/10 transition" data-testid="audio-volume-up">
          <Plus size={14} />
        </button>
        <span className="text-[10px] text-muted-warm w-8 text-center tabular-nums" data-testid="audio-volume-label">{muted ? "—" : `${Math.round(volume * 100)}%`}</span>
      </div>
    </>
  );
}
